/**
 * Integrations Page — Redesigned
 *
 * Replaces the old card-grid layout with a sortable table (ported from EBTimeEntry).
 * Company detail sheet opens on row click.
 * External Calendar sync has been moved to Settings > Settings.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Building2,
  Plus,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Search,
  Users,
  FolderKanban,
  Clock,
  Plug,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { CompanySheet } from '@/components/companies/CompanySheet';
import * as integrationsService from '@/services/integrationsService';
import { mockIntegrationsService } from '@/data/mockIntegrationsData';
import { useDataMode } from '@/contexts/DataModeContext';
import { useCompany } from '@/contexts/CompanyContext';
import {
  HR_PRODUCT_LABELS,
  PROJECT_PRODUCT_LABELS,
  TIMESHEET_PRODUCT_LABELS,
} from '@/types/integrations';
import type {
  CompanyWithIntegrations,
  SimpleIntegrationConfig,
  HRProduct,
  ProjectProduct,
  TimesheetProduct,
  IntegrationConnectionStatus,
} from '@/types/integrations';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatProductName(
  product: SimpleIntegrationConfig['product'],
  type: 'hr' | 'project' | 'timesheet'
): string {
  if (!product) return '\u2014';
  if (type === 'hr') {
    return HR_PRODUCT_LABELS[product as HRProduct] ?? product;
  }
  if (type === 'project') {
    return PROJECT_PRODUCT_LABELS[product as ProjectProduct] ?? product;
  }
  return TIMESHEET_PRODUCT_LABELS[product as TimesheetProduct] ?? product;
}

interface IntegrationCellProps {
  product: SimpleIntegrationConfig['product'];
  status: IntegrationConnectionStatus;
  type: 'hr' | 'project' | 'timesheet';
}

function IntegrationCell({ product, status, type }: IntegrationCellProps) {
  if (!product) {
    return (
      <span className="text-sm text-muted-foreground italic">Not configured</span>
    );
  }

  const label = formatProductName(product, type);

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium">{label}</span>
      <ConnectionStatusBadge status={status} compact />
    </div>
  );
}

interface ConnectionStatusBadgeProps {
  status: IntegrationConnectionStatus;
  compact?: boolean;
}

function ConnectionStatusBadge({ status, compact = false }: ConnectionStatusBadgeProps) {
  switch (status) {
    case 'connected':
      return (
        <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50 gap-1">
          <CheckCircle2 className="h-3 w-3" />
          {!compact && 'Connected'}
        </Badge>
      );
    case 'error':
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          {!compact && 'Error'}
        </Badge>
      );
    case 'not_connected':
      return (
        <Badge variant="secondary" className="gap-1">
          <XCircle className="h-3 w-3" />
          {!compact && 'Not Connected'}
        </Badge>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Loading skeleton rows
// ---------------------------------------------------------------------------

function TableRowSkeleton() {
  return (
    <tr className="border-b">
      <td className="px-4 py-3"><Skeleton className="h-4 w-32" /></td>
      <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
      <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
      <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
      <td className="px-4 py-3"><Skeleton className="h-4 w-8" /></td>
      <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
      <td className="px-4 py-3"><Skeleton className="h-4 w-4" /></td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function Integrations() {
  const { getScreenMode } = useDataMode();
  const isDemo = getScreenMode('settings') === 'demo';
  const { refreshCompanies } = useCompany();
  const [companies, setCompanies] = useState<CompanyWithIntegrations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithIntegrations | null>(null);
  const [showCreateSheet, setShowCreateSheet] = useState(false);

  const loadCompanies = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = isDemo
        ? await mockIntegrationsService.getCompanies()
        : await integrationsService.getCompanies();
      // Sort: Active first (alphabetical), then Inactive (alphabetical)
      const sorted = [...data].sort((a, b) => {
        if (a.status === b.status) return a.name.localeCompare(b.name);
        return a.status === 'active' ? -1 : 1;
      });
      setCompanies(sorted);
    } catch (error) {
      console.error('Failed to load companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  }, [isDemo]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const filteredCompanies = useMemo(() => {
    return companies.filter((company) => {
      const matchesSearch =
        company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        company.code.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus =
        selectedStatus === 'all' || company.status === selectedStatus;
      return matchesSearch && matchesStatus;
    });
  }, [companies, searchQuery, selectedStatus]);

  async function handleCompanyCreated(created: CompanyWithIntegrations) {
    await loadCompanies();
    setShowCreateSheet(false);
    setSelectedCompany(created);
    refreshCompanies();
  }

  function handleCompanyClick(company: CompanyWithIntegrations) {
    setSelectedCompany(company);
  }

  function handleSheetCompanyUpdate(updated: CompanyWithIntegrations) {
    setCompanies((prev) =>
      prev.map((c) => (c.id === updated.id ? updated : c))
    );
    setSelectedCompany(updated);
    // Refresh the shared CompanyContext so that SchedulerContext and other
    // consumers (e.g. hasProjectIntegration guard) pick up the change
    // immediately without requiring a full browser tab reload.
    refreshCompanies();
  }

  return (
    <div className="space-y-6 w-full">
      {/* Section Heading */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Plug className="h-6 w-6 text-primary" />
          <h2 className="text-3xl font-bold tracking-tight">Integrations</h2>
        </div>
        <Button onClick={() => setShowCreateSheet(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Company
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={selectedStatus}
          onValueChange={(v) => setSelectedStatus(v as 'all' | 'active' | 'inactive')}
        >
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Company List Table */}
      <div className="rounded-lg border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Company</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  HR
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <FolderKanban className="h-3.5 w-3.5" />
                  Project
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Timesheet
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  Users with Access
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 w-10" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} />)
            ) : filteredCompanies.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-16 text-center text-muted-foreground">
                  <Building2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No companies found</p>
                  {searchQuery && (
                    <p className="text-xs mt-1">
                      Try a different search term or{' '}
                      <button
                        className="underline"
                        onClick={() => setSearchQuery('')}
                      >
                        clear the filter
                      </button>
                    </p>
                  )}
                </td>
              </tr>
            ) : (
              filteredCompanies.map((company) => (
                <tr
                  key={company.id}
                  className={cn(
                    'border-b last:border-0 cursor-pointer transition-colors hover:bg-muted/50',
                    selectedCompany?.id === company.id && 'bg-muted/60'
                  )}
                  onClick={() => handleCompanyClick(company)}
                >
                  {/* Company Name + Code */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground flex-shrink-0">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-medium leading-tight">{company.name}</div>
                        <div className="text-xs text-muted-foreground">{company.code}</div>
                      </div>
                    </div>
                  </td>

                  {/* HR Integration */}
                  <td className="px-4 py-3">
                    <IntegrationCell
                      product={company.hrIntegration.product}
                      status={company.hrIntegration.status}
                      type="hr"
                    />
                  </td>

                  {/* Project Integration */}
                  <td className="px-4 py-3">
                    <IntegrationCell
                      product={company.projectIntegration.product}
                      status={company.projectIntegration.status}
                      type="project"
                    />
                  </td>

                  {/* Timesheet Integration */}
                  <td className="px-4 py-3">
                    <IntegrationCell
                      product={company.timesheetIntegration.product}
                      status={company.timesheetIntegration.status}
                      type="timesheet"
                    />
                  </td>

                  {/* Users with Access Count */}
                  <td className="px-4 py-3">
                    <span className="text-sm tabular-nums">{company.userCount}</span>
                  </td>

                  {/* Status Badge */}
                  <td className="px-4 py-3">
                    <Badge
                      variant={company.status === 'active' ? 'default' : 'secondary'}
                      className="capitalize"
                    >
                      {company.status}
                    </Badge>
                  </td>

                  {/* Chevron */}
                  <td className="px-4 py-3">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Company Detail Sheet */}
      <CompanySheet
        company={selectedCompany}
        open={!!selectedCompany}
        onOpenChange={(open) => { if (!open) setSelectedCompany(null); }}
        onCompanyUpdate={handleSheetCompanyUpdate}
        isDemo={isDemo}
      />

      {/* Add Company Sheet */}
      <CompanySheet
        company={null}
        open={showCreateSheet}
        onOpenChange={setShowCreateSheet}
        onCompanyUpdate={() => {}}
        mode="create"
        onCompanyCreated={handleCompanyCreated}
        existingCodes={companies.map((c) => c.code.toUpperCase())}
        isDemo={isDemo}
      />
    </div>
  );
}
